import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Vitro.css';

// Images
import FedRAMP from '../Public/Images/FedRAMP.png';
import Fisma from '../Public/Images/Fisma.webp';
import Fips from '../Public/Images/Fips.png';
import Nist from '../Public/Images/NIST.png';
import Section508 from '../Public/Images/Section508.webp';

function Vitro() {
    const [activePopup, setActivePopup] = useState(null);
    const [isDark, setIsDark] = useState(() => {
        try { return document.documentElement.getAttribute('data-theme') === 'dark'; } catch (e) { return false; }
    });
    const rootRef = useRef(null);

    const openPopup = (index) => {
        setActivePopup(index);
    };

    const closePopup = () => {
        setActivePopup(null);
    };

    const features = [
        {
            title: 'Containerized Services',
            summary: 'Consistent, lightweight OpenStack services for faster deployments.',
            details: (
                <>
                    <h4>Kolla is the OpenStack Game Changer</h4>
                    <p>Kolla OpenStack changes the paradigm by containerizing OpenStack itself. Instead of heavyweight, brittle deployments that take weeks to stabilize, Kolla delivers OpenStack services as Docker containers with Ansible automation. This slashes complexity, speeds up Day 1 installs and Day 2 upgrades, and makes OpenStack behave like a modern, cloud-native platform. The result is a leaner, more resilient OpenStack that reduces operational overhead and puts you in control of a production-ready private cloud in hours, not months.</p>
                    {/*<h4>Why Kolla?</h4>*/}
                    <ul>
                        <li>Containers isolate services, ensuring safer upgrades and rollbacks compared to package-based installs.</li>
                        <li>Ansible-driven lifecycle management (deploy, reconfigure, upgrade, destroy) is idempotent and CI/CD-friendly.</li>
                        <li>First-class TLS support secures API and inter-service traffic.</li>
                        <li>Ceph integration via native tools (cephadm/ceph-ansible) ensures clean storage separation.</li>
                        <li>Frequent updates (e.g., 2025.1 optimizations) maintain a steady release cadence.</li>
                    </ul>
                    <h4>Eupraxia Labs Enhancements</h4>
                    <ul>
                        <li><strong>Golden Stacks</strong>: Hardened, version-pinned manifests and defaults for OpenStack services, pre-vetted for releases like 2024.2 and 2025.1.</li>
                        <li><strong>GitOps</strong>: Configurations in Git, managed via ArgoCD/GitLab with pre-flight checks and drift detection.</li>
                        <li><strong>TLS & Identity</strong>: Automated cert provisioning and Keystone⇄Keycloak OIDC for seamless SSO/CAC integration.</li>
                        <li><strong>Ceph Integration</strong>: Ceph provisioned via cephadm, integrated with OpenStack for reliable storage.</li>
                        <li><strong>Networking</strong>: Open Virtual Network (OVN) is an SDN platform. When used with OpenStack the overall solution is known as “Neutron ML2+OVN”. OVN extends the existing capabilities of a solution based solely on Open vSwitch, which is known as “Neutron ML2+OVS”.</li>
                        <li><strong>Upgrades</strong>: Tested release tracks with automated backups for low-risk rolling restarts.</li>
                        <li><strong>Observability</strong>: Dashboards and alerts for API health, linked to actionable runbooks.</li>
                        <li><strong>Day-2 Self-Service</strong>: Simplified node/service addition and failure recovery via single commands.</li>
                        <li><strong>Install Paths</strong>: All-in-one (AIO) for labs and multinode HA for production, both leveraging Kolla/Kayobe.</li>
                    </ul>
                    <p>With Eupraxia Labs' Fleet Management Cluster (FMC) and Kolla, Federal Frontier VitroAI™ provides an easily managed on-premise solution, delivering secure, scalable infrastructure in hours, with Git-driven operations and robust day-2 support.</p>
                </>
            )
        },
        {
            title: 'GitOps-Native Management',
            summary: 'Manage upgrades and configurations through Git, eliminating brittle scripts.',
            details: (
                <>
                    <h4>Why GitOps?</h4>
                    <ul>
                        <li><strong>Single Source of Truth</strong>: All infrastructure policies and application manifests are stored in Git, ensuring full traceability.</li>
                        <li><strong>Automated Consistency</strong>: ArgoCD continuously syncs the desired state with the running state of your clusters, eliminating configuration drift.</li>
                        <li><strong>Audit & Compliance Ready</strong>: Every change is logged as a Git commit, aligning with FIPS, NIST, HIPAA, and PCI-DSS standards.</li>
                        <li><strong>Faster Rollouts, Safer Rollbacks</strong>: Deploy at scale in minutes and instantly roll back to a known-good state when needed.</li>
                    </ul>
                    <h4>How ArgoCD Works in Our Platform</h4>
                    <ul>
                        <li><strong>Version Control as the Brain</strong>: All manifests and policies are stored in Git repositories, with changes triggering automated pipelines.</li>
                        <li><strong>Continuous Reconciliation</strong>: ArgoCD agents monitor Kubernetes clusters to ensure the live state matches the declared Git state.</li>
                        <li><strong>Secure Policy Enforcement</strong>: Role-based access via Keycloak or CAC/PIV integration ensures only authorized commits flow into production.</li>
                        <li><strong>Unified Observability</strong>: Integrated with Grafana, Loki, Tempo, and Mimir for real-time monitoring and auditing of deployments.</li>
                    </ul>
                    <h4>Benefits for Your Organization</h4>
                    <ul>
                        <li><strong>Reduced Operational Overhead</strong>: Eliminates manual <code>kubectl apply</code> commands and one-off scripts.</li>
                        <li><strong>Built-In Governance</strong>: GitOps policies enforce compliance automatically.</li>
                        <li><strong>Developer Empowerment</strong>: Teams focus on coding while ArgoCD handles deployment.</li>
                        <li><strong>Enterprise Scale</strong>: Manage fleets of clusters across hybrid, edge, or multi-cloud environments with ease.</li>
                    </ul>
                    <h4>Eupraxia Labs Advantage</h4>
                    <ul>
                        <li><strong>Pre-built Pipelines</strong>: Secure cluster bootstrapping with optimized workflows.</li>
                        <li><strong>Multi-tenant GitOps</strong>: Tailored for federal and enterprise compliance requirements.</li>
                        <li><strong>Rollback & Drift Detection</strong>: Seamless rollback and drift detection for thousands of workloads.</li>
                        <li><strong>AI-Assisted Validation</strong>: PioneerAI™ catches misconfigurations before they reach production.</li>
                    </ul>
                    <p>With ArgoCD integrated into our Federal Frontier Platform™, GitOps delivers speed, security, and simplicity, reimagining Kubernetes management for mission-critical environments.</p>
                </>
            )
        },
        {
            title: 'Ceph-Backed Resilience',
            summary: 'Enterprise-grade block and object storage with high availability.',
            details: (
                <>
                    <h4>What is Ceph?</h4>
                    <p>Ceph is a highly scalable, open-source distributed storage system supporting object, block, and file storage (CephFS, RBD, RGW) in a unified platform. Known for its resilience, self-healing, and scalability to thousands of nodes, Ceph provides flexible storage solutions for enterprise and federal environments.</p>
                    <h4>Challenges with Ceph</h4>
                    <ul>
                        <li>Managing multiple daemons (monitors, managers, OSDs, metadata servers).</li>
                        <li>Configuring CRUSH maps for optimal data placement, performance, and recovery.</li>
                        <li>Tuning performance parameters (IO, network settings) for specific workloads.</li>
                        <li>Monitoring health and rebalancing to prevent data loss during scaling.</li>
                        <li>Usability issues requiring deep expertise for dashboards and configuration.</li>
                    </ul>
                    <h4>Eupraxia Labs Simplifications</h4>
                    <ul>
                        <li><strong>User Interface & Visualization</strong>: Outpost GUI with resizable columns, hyperscaler icons, and refresh controls for intuitive management.</li>
                        <li><strong>Simplified Configuration</strong>: Guided workflows wrap CLI commands and configurations, reducing complexity and misconfiguration risks.</li>
                        <li><strong>Automated Monitoring & Updates</strong>: Refresh buttons and hyperscaler visibility in Outpost UI ensure current cluster state and early anomaly detection.</li>
                        <li><strong>Scaling & Change Management</strong>: Tools for error-free scaling and controlled capacity additions, minimizing performance degradation.</li>
                        <li><strong>Unified Operational Experience</strong>: Consolidated dashboards, configuration, status, alerting, and logs for streamlined management.</li>
                    </ul>
                    <h4>Business Benefits</h4>
                    <ul>
                        <li><strong>Faster Time to Value</strong>: Reduced setup time and fewer mistakes accelerate deployment.</li>
                        <li><strong>Lower Operational Risk</strong>: Simplified workflows minimize misconfiguration and data loss.</li>
                        <li><strong>Reduced Expertise Needs</strong>: Non-experts can manage Ceph effectively.</li>
                        <li><strong>Better Visibility</strong>: Enhanced UI and monitoring predict issues before failures.</li>
                        <li><strong>Painless Scalability</strong>: Smooth expansion as storage needs grow.</li>
                    </ul>
                    <p>Eupraxia Labs’ tools transform Ceph into a user-friendly, resilient storage solution, ensuring high availability and simplified management for mission-critical federal and enterprise workloads.</p>
                </>
            )
        },
        {
            title: 'Zero Trust Security',
            summary: 'CAC/PIV integration, encrypted networking, and storage.',
            details: (
                <>
                    <h4>Core Principles of Zero Trust</h4>
                    <ul>
                        <li><strong>Never Trust, Always Verify</strong>: Every user, device, and workload must prove identity and authorization, regardless of network location.</li>
                        <li><strong>Least-Privilege Access</strong>: Granular, role-based controls limit access to reduce lateral movement risks.</li>
                        <li><strong>Continuous Authentication</strong>: CAC/PIV, MFA, and behavioral analytics ensure ongoing trust validation.</li>
                        <li><strong>Data-Centric Security</strong>: FIPS 140-3 encryption and tokenization protect data across clouds, clusters, and endpoints.</li>
                        <li><strong>Assume Breach</strong>: Architectures designed for adversary presence, with automated detection and AI-driven anomaly spotting.</li>
                    </ul>
                    <h4>Zero Trust in Action</h4>
                    <ul>
                        <li><strong>Identity-Centric Controls</strong>: Keycloak and optional OIDC federation for secure, policy-driven identity management.</li>
                        <li><strong>Microsegmentation</strong>: Isolates workloads at container, VM, and node levels to prevent lateral attacks.</li>
                        <li><strong>AI-Enhanced Threat Detection</strong>: PioneerAI™ scans telemetry, logs, and behavior for drift or malicious activity.</li>
                        <li><strong>End-to-End Observability</strong>: Grafana, Loki, and Tempo provide real-time security posture insights.</li>
                        <li><strong>Policy-Driven Automation</strong>: GitOps pipelines via ArgoCD/GitLab embed security in every release.</li>
                    </ul>
                    <h4>Why Choose Eupraxia Labs</h4>
                    <ul>
                        <li><strong>Federal-Grade Security</strong>: Aligned with NIST 800-53, DISA STIG, FedRAMP, HIPAA, and PCI-DSS.</li>
                        <li><strong>Open & Flexible</strong>: Built on open standards, avoiding vendor lock-in.</li>
                        <li><strong>AI-Ready</strong>: Scales with GPU-powered workloads and edge/multi-cloud deployments.</li>
                        <li><strong>Mission-Critical Reliability</strong>: Designed for defense, healthcare, and enterprises requiring zero downtime or compromise.</li>
                    </ul>
                    <p>Zero Trust is woven into the fabric of Eupraxia Labs’ platforms, delivering robust security for mission-critical environments without compromising flexibility or performance.</p>
                </>
            )
        }
    ];

    useEffect(() => {
        const apply = (darkNow) => {
            try {
                if (!rootRef.current) return;
                const selectors = [
                    '.hero-section',
                    '.why-section',
                    '.features-section',
                    '.compliance-section',
                    '.cta-section',
                    '.cta-section-vitro',
                    '.screenshots-section',
                    '.why-section',
                ];
                if (darkNow) rootRef.current.classList.add('dark'); else rootRef.current.classList.remove('dark');
                selectors.forEach(sel => {
                    const el = rootRef.current.querySelector(sel);
                    if (!el) return;
                    if (darkNow) {
                        el.style.setProperty('background', '#16171a', 'important');
                        el.style.setProperty('background-image', 'none', 'important');
                        el.style.setProperty('color', '#e6eef8', 'important');
                    } else {
                        el.style.removeProperty('background');
                        el.style.removeProperty('background-image');
                        el.style.removeProperty('color');
                    }
                });
                // ensure cards and modals use card-bg in dark
                Array.from(rootRef.current.querySelectorAll('.feature-card, .column-vitro, .compliance-column-vitro, .popup-content')).forEach(el => {
                    if (darkNow) {
                        el.style.setProperty('background', 'var(--card-bg)', 'important');
                        el.style.setProperty('background-image', 'none', 'important');
                        el.style.setProperty('color', 'var(--text)', 'important');
                    } else {
                        el.style.removeProperty('background');
                        el.style.removeProperty('background-image');
                        el.style.removeProperty('color');
                    }
                });
            } catch (e) { /* ignore */ }
        };

        const obs = new MutationObserver(() => {
            const darkNow = document.documentElement.getAttribute('data-theme') === 'dark';
            setIsDark(darkNow);
            apply(darkNow);
        });
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        // initial
        apply(document.documentElement.getAttribute('data-theme') === 'dark');
        return () => obs.disconnect();
    }, []);

    // Inline style (highest-priority) for hero and bottom CTA to guarantee dark background
    const sectionStyle = isDark ? { background: '#16171a', backgroundImage: 'none', color: '#e6eef8' } : {};

    return (
        <div ref={rootRef} className={`vitro-page ${isDark ? 'dark' : ''}`}>
            <section className="hero-section" style={sectionStyle}>
                <div className="hero-content">
                    <h1>Mission-Ready Infrastructure with Compliance Built In</h1>
                    <p>VitroAI™ provides the technical foundation and controls required for agencies to meet FedRAMP, FISMA, DISA-STIG, and NIST 800-53 requirements. While compliance certifications are earned through audits, our platform shortens that process with pre-integrated, hardened, and auditable systems.</p>
                    <div className="cta-buttons">
                        <Link to="/contact" className="cta-button-vitro">Explore Government Solutions</Link>
                    </div>
                </div>
            </section>
            <section className="why-section">
                <h2>Why Choose VitroAI™</h2>
                <div className="columns-container">
                    <div className="column-vitro">
                        <span className="icon" aria-label="Containerized services icon">📦</span>
                        <h3>Containerized Services</h3>
                        <p>Lightweight, consistent deployments with minimal operational drift.</p>
                    </div>
                    <div className="column-vitro">
                        <span className="icon" aria-label="GitOps management icon">📜</span>
                        <h3>GitOps-Native Management</h3>
                        <p>Declarative, auditable changes via Git for seamless operations.</p>
                    </div>
                    <div className="column-vitro">
                        <span className="icon" aria-label="Enterprise hardening icon">🔒</span>
                        <h3>Enterprise Hardening</h3>
                        <p>CAC-enabled UI, FIPS-aligned builds, and automated monitoring.</p>
                    </div>
                </div>
            </section>
            <section className="features-section">
                <h2>Features</h2>
                <div className="features-container-vitro">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="feature-card"
                            onClick={() => openPopup(index)}
                        >
                            <h3>{feature.title}</h3>
                            <p className="summary">{feature.summary}</p>
                        </div>
                    ))}
                </div>
            </section>
            {activePopup !== null && (
                <div className="popup-overlay" onClick={closePopup}>
                    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                        <button className="popup-close" onClick={closePopup}>×</button>
                        <h3>{features[activePopup].title}</h3>
                        <div className="popup-details">
                            {features[activePopup].details}
                        </div>
                    </div>
                </div>
            )}
            <section className="compliance-section">
                <h2>Compliance Without the Complexity</h2>
                <div className="compliance-container">
                    <div className="compliance-column-vitro">
                        <h3>What We Provide</h3>
                        <ul>
                            <li>Pre-hardened images aligned with DISA-STIG</li>
                            <li>FIPS 140-2/3 validated cryptography libraries</li>
                            <li>Integrated monitoring, logging, and identity tools</li>
                            <li>Documentation to map controls to frameworks like NIST 800-53</li>
                        </ul>
                    </div>
                    <div className="compliance-column-vitro">
                        <h3>What You Achieve Faster</h3>
                        <ul>
                            <li>FedRAMP ATOs</li>
                            <li>FISMA system authorizations</li>
                            <li>Easier NIST audits</li>
                            <li>Reduced compliance costs and timelines</li>
                        </ul>
                    </div>
                </div>
                <div className="badge-bar">
                    <div className="badge-item">
                        <img src={FedRAMP} alt="FedRAMP compliant" className="badge" />
                        <span className="badge-label">FedRAMP</span>
                    </div>
                    <div className="badge-item">
                        <img src={Fisma} alt="FISMA compliant" className="badge" />
                        <span className="badge-label">FISMA</span>
                    </div>
                    <div className="badge-item">
                        <img src={Fips} alt="FIPS 140-2/3 compliant" className="badge" />
                        <span className="badge-label">FIPS 140-2/3</span>
                    </div>
                    <div className="badge-item">
                        <img src={Nist} alt="NIST 800-53 compliant" className="badge" />
                        <span className="badge-label">NIST 800-53</span>
                    </div>
                    <div className="badge-item">
                        <img src={Section508} alt="Section 508 compliant" className="badge" />
                        <span className="badge-label">Section 508</span>
                    </div>
                </div>
                </section>
            <section className="cta-section-vitro" style={sectionStyle}>
                <h2>Ready to modernize your mission systems?</h2>
                <Link to="/contact" className="cta-button-vitro">Contact Our Government Team</Link>
            </section>
        </div>
    );
}

export default Vitro;

